/**
 * The quick-glance connectivity panel, opened from the candidate list's "Connect" button.
 *
 * WHAT THIS USED TO BE. Four hard-coded cards — Gmail, Outlook, a webhook and an invented
 * address `intake+recruiting@talenthop.in` — with `alert()` behind every button and green
 * "Connected · Syncing every 5m" labels on integrations that did not exist. It claimed four live
 * connections where there were none.
 *
 * WHAT IT IS NOW. A read-only summary of the org's REAL connectors, from the same
 * GET /orgs/{orgId}/connectors the Connections page reads. Configuring, testing and
 * enabling all happen on that page: this panel gets you there, it does not duplicate it. That
 * is the whole point of the connector framework — one place integrations are managed, not a
 * second copy of the controls that can drift out of step with the first.
 */
import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  CONNECTOR_STATUS_LABELS,
  INBOUND_EMAIL_DOMAIN,
  type ConnectorStatus,
} from "@talenthop/shared";
import { api } from "../lib/api.js";
import { useAuth } from "../lib/AuthContext.js";
import { Link2, Mail, X, Info } from "./icons.js";

export interface ConnectivityModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface ConnectorSummary {
  id: string;
  typeKey: string;
  typeLabel: string;
  name: string;
  status: ConnectorStatus;
  enabled: boolean;
  wired: boolean;
  provisioning: "native" | "external_subscription";
  lastActivityAt: string | null;
}

const statusPill = (s: ConnectorStatus): string =>
  s === "active" ? "pill v" : s === "error" ? "pill f" : s === "connected" ? "pill pv" : "pill n";

export function ConnectivityModal({ isOpen, onClose }: ConnectivityModalProps): ReactNode {
  const { me } = useAuth();
  const orgId = me?.orgId ?? "";

  const { data, isLoading } = useQuery({
    queryKey: ["connectors", orgId],
    queryFn: () => api.get<{ connectors: ConnectorSummary[] }>(`/orgs/${orgId}/connectors`),
    enabled: isOpen && orgId.length > 0,
  });
  const connectors = data?.connectors ?? [];
  const emailReceive = connectors.find((c) => c.typeKey === "email_receive");

  if (!isOpen) return null;

  return (
    <div className="customer-modal-backdrop" onClick={onClose}>
      <div
        className="customer-modal-card"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label="Connections"
      >
        <div className="customer-modal-header">
          <div className="modal-title-box">
            <div className="modal-icon-badge">
              <Link2 />
            </div>
            <div>
              <h3 className="modal-title">Connections</h3>
              <p className="modal-sub">Where your candidates come in from.</p>
            </div>
          </div>
          <button type="button" className="modal-close-btn" onClick={onClose} aria-label="Close modal">
            <X style={{ width: 18, height: 18 }} />
          </button>
        </div>

        <div style={{ padding: 20 }}>
          {isLoading && <div className="mut sm">Loading your connections…</div>}

          {!isLoading && connectors.length === 0 && (
            <div className="banner info">
              <Info />
              <div>
                No connections configured yet. Email receiving gives every job its own address on{" "}
                <b>{INBOUND_EMAIL_DOMAIN}</b> that applications can be forwarded to — set it up on
                the Connections page.
              </div>
            </div>
          )}

          {connectors.length > 0 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {connectors.map((c) => (
                <div
                  key={c.id}
                  className="card pad"
                  style={{ display: "flex", alignItems: "center", gap: 12 }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 650 }}>{c.name}</div>
                    <div className="mut sm">
                      {c.typeLabel}
                      {c.provisioning === "external_subscription" && " · your own account"}
                      {!c.wired && " · not live yet"}
                    </div>
                  </div>
                  {/* The real lifecycle state, not a decorative "Connected" label. */}
                  <span className={statusPill(c.status)}>{CONNECTOR_STATUS_LABELS[c.status]}</span>
                </div>
              ))}
            </div>
          )}

          {emailReceive && (
            <div className="banner info" style={{ marginTop: 14 }}>
              <Mail />
              <div>
                Each job has its own address on <b>{INBOUND_EMAIL_DOMAIN}</b> — open a job to copy
                it. Forward an application there and it's imported against that job.
              </div>
            </div>
          )}
        </div>

        <div className="customer-modal-footer">
          <span className="modal-footer-hint">
            Configure, test and enable connections on the Connections page.
          </span>
          <Link to="/app/connections" className="btn-modal-done" onClick={onClose}>
            Manage connections
          </Link>
        </div>
      </div>
    </div>
  );
}
